import { ReactComponent as StatusIcon } from '@assets/components/panel/monitoring/status.svg';
import { IcStatusLosange } from '@assets/icons/24x24';
import { Box, Button, ButtonGroup, For, Grid, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import {
  criticalityCategoryFromSensorStatus,
  statusCategoryColor,
} from '@components/cockpit/panels/monitoring/monitoring.types';
import { WorstSensorStatusOption } from '@components/cockpit/panels/notificationSearch/notificationSearch.types';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import DateTimeRangeInputWithPresets from '@components/common/inputs/DateTimeRangeInputWithPresets';
import { ButtonWithIcon } from '@components/ui/buttonToggle/ButtonWithIcon';
import { DurationInput } from '@components/ui/input/DurationInput';
import { InputNumber } from '@components/ui/input/InputNumber';
import { targetColor } from '@utils/map/map.constants';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateTimeRange } from '@/types/dateTime.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';
import { ReliabilityEnum } from '@/types/sensor/identification.types';
import { ConnectorStatusEnum, HealthStatusEnum } from '@/types/sensor/sensors';

export const WorstClassificationFilter = () => {
  const { filters, setFilters } = useNotificationSearchContext();

  const handleToggleClassification = (classification: Exclude<ClassificationEnum, ClassificationEnum.UNCLASSIFIED>) => {
    const prevSet = new Set(filters.worstClassification);

    if (prevSet.has(classification)) {
      prevSet.delete(classification);
    } else {
      prevSet.add(classification);
    }

    setFilters({ worstClassification: new Set(prevSet) });
  };

  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <Text fontWeight="medium" color="neutral.300">
        <FormattedMessage id="components.notificationSearch.filters.worstClassification" />
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={1}>
        <For
          each={Object.values(ClassificationEnum).filter(
            (classification) => classification !== ClassificationEnum.UNCLASSIFIED,
          )}
        >
          {(classification) => {
            const isSelected = filters.worstClassification.has(classification);
            return (
              <ButtonWithIcon
                key={classification}
                label={`target.classification.${classification}`}
                icon={
                  <Icon as={IcStatusLosange} boxSize="24px" color={targetColor[classification]} data-custom-color />
                }
                data-active={isSelected}
                onClick={() => handleToggleClassification(classification)}
              />
            );
          }}
        </For>
      </Grid>
    </VStack>
  );
};

const worstSensorStatusOptions: WorstSensorStatusOption[] = [
  HealthStatusEnum.DEGRADED,
  HealthStatusEnum.KO,
  ConnectorStatusEnum.UNREACHABLE,
];

export const WorstStatusFilter = () => {
  const { filters, setFilters } = useNotificationSearchContext();

  const handleToggleStatus = (status: WorstSensorStatusOption) => {
    const prevSet = new Set(filters.worstSensorStatus);

    if (prevSet.has(status)) {
      prevSet.delete(status);
    } else {
      prevSet.add(status);
    }

    setFilters({ worstSensorStatus: new Set(prevSet) });
  };

  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <Text fontWeight="medium" color="neutral.300">
        <FormattedMessage id="components.notificationSearch.filters.filterOptions.worstSensorStatus" />
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={1}>
        <For each={worstSensorStatusOptions}>
          {(status) => {
            const isSelected = filters.worstSensorStatus.has(status);
            return (
              <ButtonWithIcon
                key={status}
                label={`sensors.status.${status}`}
                icon={
                  <Icon
                    as={StatusIcon}
                    boxSize="18px"
                    color={statusCategoryColor[criticalityCategoryFromSensorStatus[status]]}
                    data-custom-color
                  />
                }
                data-active={isSelected}
                onClick={() => handleToggleStatus(status)}
              />
            );
          }}
        </For>
      </Grid>
    </VStack>
  );
};

type PeriodFilterProps = {
  isDetection: boolean;
};
export const PeriodFilter = ({ isDetection }: Readonly<PeriodFilterProps>) => {
  const { formatMessage } = useIntl();

  const { filters, setFilters } = useNotificationSearchContext();
  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <Text fontWeight="medium" color="neutral.300">
        <FormattedMessage
          id={`components.notificationSearch.filters.filterOptions.${isDetection ? 'detectionPeriod' : 'incidentPeriod'}`}
        />
      </Text>
      <DateTimeRangeInputWithPresets
        range={filters.range}
        onChange={async (range: DateTimeRange) => setFilters({ range })}
        placeholder={formatMessage({
          id: `components.notificationSearch.filters.filterOptions.${isDetection ? 'detectionPeriod' : 'incidentPeriod'}`,
        })}
      />
    </VStack>
  );
};

export const MaxAltitudeOrHeightFilter = () => {
  const { filters, setFilters } = useNotificationSearchContext();
  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <ButtonGroup width="100%" height="48px" attached>
        <Button variant="toggle" size="toggleM" flex={1} data-active={filters.useAltitude} disabled>
          <FormattedMessage id="components.notificationSearch.filters.maxAltitude" />
        </Button>
        <Button variant="toggle" size="toggleM" flex={1} data-active={!filters.useAltitude}>
          <FormattedMessage id="components.notificationSearch.filters.maxHeight" />
        </Button>
      </ButtonGroup>
      <HStack gap={2}>
        <Text fontWeight="medium" color="neutral.300">
          <FormattedMessage id="global.between" />
        </Text>
        <InputNumber
          suffix="m"
          width="96px"
          min={-9999}
          max={99999}
          value={filters.minimumMaxHeight}
          onChange={(value) => setFilters({ minimumMaxHeight: value })}
        />
        <Text fontWeight="medium" color="neutral.300">
          <FormattedMessage id="global.and" />
        </Text>
        <InputNumber
          suffix="m"
          width="96px"
          min={-9999}
          max={99999}
          value={filters.maximumMaxHeight ?? ''}
          onChange={(value) => setFilters({ maximumMaxHeight: value })}
        />
      </HStack>
    </VStack>
  );
};

export const ReliabilityFilter = () => {
  const { filters, setFilters } = useNotificationSearchContext();

  const handleToggleReliability = (reliability?: ReliabilityEnum) => {
    setFilters({ reliability });
  };

  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <Text fontWeight="medium" color="neutral.300">
        <FormattedMessage id="components.notificationSearch.filters.reliability.title" />
      </Text>
      <HStack gap={0} width="100%" height="48px">
        <Button
          variant="toggle"
          size="toggleM"
          flex={1}
          zIndex={filters.reliability === ReliabilityEnum.SURE ? 1 : 0}
          _hover={{ zIndex: 2 }}
          data-active={filters.reliability === ReliabilityEnum.SURE}
          onClick={() => handleToggleReliability(ReliabilityEnum.SURE)}
        >
          <FormattedMessage id="components.notificationSearch.filters.reliability.sure" />
        </Button>
        <Button
          variant="toggle"
          size="toggleM"
          flex={1}
          marginLeft="-2px"
          zIndex={filters.reliability === ReliabilityEnum.UNSURE ? 1 : 0}
          _hover={{ zIndex: 2 }}
          data-active={filters.reliability === ReliabilityEnum.UNSURE}
          onClick={() => handleToggleReliability(ReliabilityEnum.UNSURE)}
        >
          <FormattedMessage id="components.notificationSearch.filters.reliability.unsure" />
        </Button>
        <Button
          variant="toggle"
          size="toggleM"
          flex={1}
          marginLeft="-2px"
          zIndex={filters.reliability ? 0 : 1}
          _hover={{ zIndex: 2 }}
          data-active={!filters.reliability}
          onClick={() => handleToggleReliability()}
        >
          <FormattedMessage id="components.notificationSearch.filters.reliability.all" />
        </Button>
      </HStack>
    </VStack>
  );
};

type DurationFilterProps = {
  isDetection: boolean;
};
export const DurationFilter = ({ isDetection }: Readonly<DurationFilterProps>) => {
  const { filters, setFilters } = useNotificationSearchContext();
  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <Text fontWeight="medium" color="neutral.300">
        <FormattedMessage
          id={`components.notificationSearch.filters.filterOptions.${isDetection ? 'detectionDuration' : 'treatmentDuration'}`}
        />
      </Text>
      <Box width="50%">
        <DurationInput value={filters.duration} onChange={(e) => setFilters({ duration: Number(e.target.value) })} />
      </Box>
    </VStack>
  );
};
